import { type FunctionComponent, useRef, useState } from 'react';
import {
  Button, Checkbox, Input, Link,
} from '@dr.pogodin/react-utils';

// import SocialLoginBlock from 'components/SocialLoginBlock';
import Throbber from 'components/Throbber';
import useLogin from 'hooks/useLogin';

import IconEye from 'assets/images/icon-eye.svg';
import Logo from 'assets/images/logo-soul-sanctuary-new-150px.svg';

import buttonIconTheme from 'themes/Button/ai-app/password-toggle.scss';
import buttonH48Theme from 'themes/Button/h48px.scss';
import inputTheme from 'themes/Input/ai-app/auth.scss';

import checkboxTheme from './checkbox.scss';
import S from './style.scss';

const Login: FunctionComponent = () => {
  const usernameRef = useRef<HTMLInputElement>(null);
  const passwordRef = useRef<HTMLInputElement>(null);

  const [remember, setRemember] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [showUsernameError, setShowUsernameError] = useState(false);
  const [showPasswordError, setShowPasswordError] = useState(false);

  const {
    busy,
    errorMessage,
    login,
    password,
    passwordOk,
    resetPasswordV2,
    setErrorMessage,
    setPassword,
    setUsername,
    username,
    usernameOk,
  } = useLogin();

  if (usernameOk && showUsernameError) setShowUsernameError(false);
  if (passwordOk && showPasswordError) setShowPasswordError(false);

  const resetPassword = async () => {
    setErrorMessage('If provided email is registered, you\'ll receive password reset email shortly');
    await resetPasswordV2('aiapp');
  };

  return (
    <div className={S.container}>
      <div className={S.content}>
        <Logo />
        <h1 styleName="title">Login</h1>
        <p styleName="subtitle">Login to access your Soul Sanctuary account</p>
        <form autoComplete="on" styleName="registrationForm">
          <div styleName="inputWrapper">
            <Input
              autoComplete="email"
              id="username"
              name="username"
              onBlur={() => {
                if (!usernameOk) setShowUsernameError(true);
              }}
              onChange={(e) => {
                setUsername(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (!passwordRef.current) throw Error('Missing reference');
                  if (usernameOk) passwordRef.current.focus();
                  else setShowUsernameError(true);
                }
              }}
              placeholder="Email Address"
              ref={usernameRef}
              theme={inputTheme}
              type="email"
              value={username}
            />
            {showUsernameError ? (
              <div styleName="error">
                Not a valid email.
              </div>
            ) : null}
          </div>
          <div styleName="inputWrapper">
            <Input
              autoComplete="new-password"
              id="password"
              name="password"
              onBlur={() => {
                if (!passwordOk) setShowPasswordError(true);
              }}
              onChange={(e) => {
                setPassword(e.target.value);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') {
                  if (!usernameRef.current) throw Error('Missing reference');
                  if (!passwordOk) setShowPasswordError(true);
                  else if (usernameOk) void login(remember);
                  else usernameRef.current.focus();
                }
              }}
              placeholder="Password"
              ref={passwordRef}
              theme={inputTheme}
              type={showPassword ? 'text' : 'password'}
              value={password}
            />
            <Button
              onClick={() => {
                setShowPassword(!showPassword);
              }}
              theme={buttonIconTheme}
            >
              <IconEye />
            </Button>
            {showPasswordError ? (
              <div styleName="error">
                No password provided.
              </div>
            ) : null}
          </div>
          <div styleName="rememberMeAndForgotPassword">
            <Checkbox
              checked={remember}
              label="Remember me"
              onChange={() => {
                setRemember(!remember);
              }}
              theme={checkboxTheme}
            />
            <Link
              onClick={() => {
                void resetPassword();
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                void resetPassword();
              }}
              styleName="forgotPasswordLink"
              to=""
            >
              Forgot Password
            </Link>
          </div>
          {busy ? <div styleName="throbberContainer"><Throbber /></div> : (
            <Button
              disabled={!usernameOk || !passwordOk}
              onClick={() => {
                void login(remember);
              }}
              theme={buttonH48Theme}
            >
              Log In
            </Button>
          )}
          {errorMessage ? (
            <div styleName="error">
              {errorMessage} &mdash; &zwnj;
              {/* TODO: Implement password reset. */}
              <Link
                onClick={() => {
                  void resetPassword();
                }}
                onContextMenu={(e) => {
                  e.preventDefault();
                  void resetPassword();
                }}
                styleName="link"
                to=""
              >
                Restore password
              </Link>
            </div>
          ) : null}
        </form>
        {/* <SocialLoginBlock /> */}
        <div styleName="dontHaveAnAccountBlock">
          Don&apos;t have an account yet? &zwnj;
          <Link styleName="link" to="/register">Sign Up</Link>
        </div>
      </div>
    </div>
  );
};

export default Login;
